// Bare shared-library import, not a deep workspacePrefix.js subpath — see
// app.js's identical fix note (tickets/catalog-fixall.md P0).
import { shellStripWorkspacePrefix } from './shellSharedDelegates.js'
import { queryHashTail } from './bootRedirectQuery.js'
// Deferred to call time — an eager module-scope destructure of the barrel's
// default crashes the shell boot inside the shared-barrel init cycle.

// Demo route lockdown — the single source of truth for which shell routes an
// `isDemo` session may NOT visit, plus the smbls router guard that blocks the
// navigation BEFORE anything mounts.
//
// Demo sessions are locked to their own sandbox org: admin / cross-org /
// other-org-people / internal-features surfaces are all off-limits. `/people`
// and `/team-members` are the OTHER-org people rosters reachable from the
// (now-hidden) nav + dropdown; `/data`, `/admin`, `/features` are the
// admin/internal surfaces. Matched as exact path or sub-path.
export const DEMO_BLOCKED_ROUTE_RE = /^\/(admin|data|features|people|team-members)(\/|$)/

// smbls router guard (registered via context.routerOptions.guards — see
// authGuard.js createAuthRouteGuard for the convention: `true` allows,
// `false` cancels, a string redirects). Running INSIDE the router, before
// pushState/content-set, means a blocked route never mounts at all: no
// half-rendered admin page, no helmet title write, and none of the
// re-entrant double-`set` fallout the post-mount DemoRouteGuard bounce hit
// (duplicate `content` children at the shell root — see tickets/smbls.md
// "RE-ENTRANT el.router()"). The reactive DemoRouteGuard sentinel in app.js
// stays as the fallback for the one case a navigation guard can't see:
// `isDemo` resolving to true AFTER the session is already sitting on a
// blocked route.
export const demoRouteGuard = ({ pathname, element, query, hash }) => {
  const root = element?.state?.root || element?.state
  if (!root?.isDemo) return true
  // Carry the blocked navigation's query + hash onto the bounce target — the
  // guard fires INSIDE the router before the target URL is parsed any
  // further, so `query`/`hash` here are the smbls router's own parse of the
  // path that was being navigated to (see plugins/router/index.js's
  // `guardContext`), not the page the visitor is leaving. Dropping them was
  // the same "redirect rebuilt from a bare '/' " bug as the module-route-leave
  // + demo-lockdown bounces in app.js (see functions/bootRedirectQuery.js) —
  // same class, different call site (a router GUARD string return, not
  // `el.router()`), so fixed the same way: reattach instead of discarding.
  const tail = queryHashTail(query, hash)
  // Strip any `/w/<org-slug>` prefix before the lockdown test — otherwise a
  // prefixed `/w/acme/admin` slips past the anchored `^/(admin|…)` regex and a
  // demo session reaches admin/data/features/other-org people (§6d; security).
  // No-op until Phase 3 emits the prefix.
  if (DEMO_BLOCKED_ROUTE_RE.test(shellStripWorkspacePrefix(pathname || ''))) return '/' + tail
  // Bare /studio renders the all-workspaces dashboard canvas, which is empty
  // for a fresh sandbox — but the org's studios board (/studio/<slug>) shows
  // the seeded "Acme Landing" project frame. Land the demo visitor there so
  // the header "Studio" tab always opens onto content.
  if (pathname === '/studio') {
    const slug = root.orgs?.[0]?.slug || root.org?.slug
    if (slug) return `/studio/${slug}` + tail
  }
  return true
}
